import moment from "moment";
import "moment-timezone";

export interface OutreachContactExportAttribute {
  id?: number | string;
  name: string;
  isDeleted?: boolean;
  isShowOnClient?: boolean;
}

export interface OutreachContactExportContact {
  id?: number | string;
  firstName?: string;
  lastName?: string;
  channel?: string;
  primaryContact?: string;
  tags?: Array<{ name?: string }>;
  stage?: { name?: string };
  country?: string;
  contactattributes?: Array<{
    id?: number | string;
    name?: string;
    contactattributevalue?: {
      value?: string;
    };
  }>;
}

export const CSV_FORMULA_INJECTION_PREFIX_PATTERN = /^[=+\-@\t\r\n]/;

export const getExportableContactAttributes = (
  contactAttributes: OutreachContactExportAttribute[] = []
) => contactAttributes.filter((attribute) => attribute.isShowOnClient && !attribute.isDeleted);

export const sanitizeCsvValue = (value: unknown): string => {
  const stringValue = value === undefined || value === null ? "" : `${value}`;

  if (CSV_FORMULA_INJECTION_PREFIX_PATTERN.test(stringValue)) {
    return `'${stringValue}`;
  }

  return stringValue;
};

export const escapeCsvValue = (value: unknown): string => {
  const sanitizedValue = sanitizeCsvValue(value);
  return `"${sanitizedValue.replace(/"/g, '""')}"`;
};

export const buildCsvContent = (rows: unknown[][]): string => {
  const csvContent = rows
    .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
    .join("\r\n");

  return `\ufeff${csvContent}`;
};

export const formatContactTags = (tags: OutreachContactExportContact["tags"] = []) =>
  tags
    .map((tag) => tag.name)
    .filter((name) => name)
    .join(", ");

export const getContactAttributeExportValue = (
  contact: OutreachContactExportContact,
  attributeDefinition: OutreachContactExportAttribute,
  dateAttribute: string[] = [],
  timezone = moment.tz.guess()
) => {
  const attribute = (contact.contactattributes || []).find((contactAttribute) => {
    const hasMatchingId =
      contactAttribute.id !== undefined &&
      attributeDefinition.id !== undefined &&
      `${contactAttribute.id}` === `${attributeDefinition.id}`;

    return hasMatchingId || contactAttribute.name === attributeDefinition.name;
  });
  let value = attribute?.contactattributevalue?.value || "";
  const dateAttributeNames = dateAttribute.map((attributeName) => attributeName.toLowerCase());

  if (
    dateAttributeNames.includes(attributeDefinition.name.toLowerCase()) &&
    value &&
    moment(value).isValid()
  ) {
    value = moment(value).tz(timezone).format("YYYY-MM-DD HH:mm:ssZ");
  }

  return value;
};

export const getOutreachContactExportRows = ({
  contacts,
  contactAttributes,
  dateAttribute,
  timezone,
}: {
  contacts: OutreachContactExportContact[];
  contactAttributes: OutreachContactExportAttribute[];
  dateAttribute?: string[];
  timezone?: string;
}) => {
  const exportableAttributes = getExportableContactAttributes(contactAttributes);
  const headers = [
    "ID",
    "First Name",
    "Last Name",
    "Channel",
    "Primary Contact",
    "Tags",
    "Stage",
    "Country",
    ...exportableAttributes.map((attribute) => attribute.name),
  ];
  const rows = contacts.map((contact) => [
    contact.id || "",
    contact.firstName || "",
    contact.lastName || "",
    contact.channel || "",
    contact.primaryContact || "",
    formatContactTags(contact.tags),
    contact.stage?.name || "",
    contact.country || "",
    ...exportableAttributes.map((attribute) =>
      getContactAttributeExportValue(contact, attribute, dateAttribute, timezone)
    ),
  ]);

  return [headers, ...rows];
};
